import React, { useState, useEffect, useRef, memo, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './StocksPage.module.css';
import { FaRegChartBar } from "react-icons/fa";
import * as api from '../../utils/api';
import { ThemeContext } from '../../context/ThemeContext';
import { formatSymbol } from '../../utils/symbolFormatter';
import { usePrice } from '../../context/PriceContext';

const StockCard = memo(({ symbol, onCardClick }) => {
    const { price: livePrice, lastDayClose } = usePrice(symbol);

    const [priceChange, setPriceChange] = useState(0);
    const [priceChangePercent, setPriceChangePercent] = useState(0);
    const [showImage, setShowImage] = useState(true);

    useEffect(() => {
        if (livePrice != null && lastDayClose != null && lastDayClose !== 0) {
            const change = livePrice - lastDayClose;
            const changePercent = (change / lastDayClose) * 100;
            setPriceChange(change);
            setPriceChangePercent(changePercent);
        }
    }, [livePrice, lastDayClose]);

    const displayPrice = livePrice != null ? livePrice.toFixed(2) : '--';
    const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : '');
    const logoPath = `/logos/${symbol.toLowerCase().replace(/\s+/g, '_')}.svg`;

    const handleImageError = () => {
        setShowImage(false);
    };

    return (
        <div className={styles['stock-card']} onClick={onCardClick}>
            <div className={styles['stock-logo-container']}>
                {showImage ? (
                    <img
                        src={logoPath}
                        alt={`${symbol} logo`}
                        className={styles['stock-logo']}
                        onError={handleImageError}
                    />
                ) : (
                    <div className={styles['stock-card-initial']}>{getInitials(symbol)}</div>
                )}
            </div>
            <h4>{formatSymbol(symbol)}</h4>
            <p className={styles.price}>₹{displayPrice}</p>
            <p className={`${styles.change} ${priceChange > 0 ? styles.positive : priceChange < 0 ? styles.negative : ''}`}>
                {priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
            </p>
        </div>
    );
});

const StockRow = ({ symbol, onRowClick, onIconClick, disableLogo }) => {
    const { price: livePrice, lastDayClose } = usePrice(symbol);

    const [priceChange, setPriceChange] = useState(0);
    const [priceChangePercent, setPriceChangePercent] = useState(0);
    const [showImage, setShowImage] = useState(true);

    useEffect(() => {
        if (livePrice != null && lastDayClose != null && lastDayClose !== 0) {
            const change = livePrice - lastDayClose;
            const changePercent = (change / lastDayClose) * 100;
            setPriceChange(change);
            setPriceChangePercent(changePercent);
        }
    }, [livePrice, lastDayClose]);

    const displayPrice = livePrice != null ? livePrice.toFixed(2) : '--';
    const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : '');
    const logoPath = disableLogo ? null : `/logos/${symbol.toLowerCase().replace(/\s+/g, '_')}.svg`;

    const handleImageError = () => {
        setShowImage(false);
    };

    return (
        <div className={styles['stock-row']}>
            <div className={styles['stock-info']} onClick={onRowClick}>
                <div className={styles['stock-logo-container']}>
                    {showImage && logoPath ? (
                        <img
                            src={logoPath}
                            alt={`${symbol} logo`}
                            className={styles['stock-logo']}
                            onError={handleImageError}
                        />
                    ) : (
                        <div className={styles['stock-initial']}>{getInitials(symbol)}</div>
                    )}
                </div>
                <div className={styles['stock-name-and-price']}>
                    <p className={styles['stock-name']}>{formatSymbol(symbol)}</p>
                    <div className={styles['stock-price-info']}>
                        <p className={styles['stock-price']}>₹{displayPrice}</p>
                        <p className={`${styles['stock-change']} ${priceChange > 0 ? styles.positive : priceChange < 0 ? styles.negative : ''}`}>
                            {priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles['chart-icon']} onClick={onIconClick}>
                <FaRegChartBar />
            </div>
        </div>
    );
};

// --- MAIN StocksPage ---
const StocksPage = () => {
    const [indices, setIndices] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [topTradedEquity, setTopTradedEquity] = useState([]);
    const [loading, setLoading] = useState(true);

    const { theme } = useContext(ThemeContext);
    const topTradedRef = useRef(null);
    const navigate = useNavigate();

    // --- Fetch all symbols and set up sections (Aligned with old_frontend) ---
    useEffect(() => {
        const fetchSymbols = async () => {
            try {
                const { data: symbols } = await api.getSymbols();
                
                // Use sr_no logic from old_frontend
                const newIndices = symbols.filter((s) => ['1', '2', '3', '4'].includes(String(s.sr_no))).map(s => s.user_symbol);
                const newStocks = symbols.filter((s) => ['5', '6', '7', '10'].includes(String(s.sr_no))).map(s => s.user_symbol);
                const newTopTradedEquity = symbols.filter((s) => ['8', '249', '296', '110', '130', '151', '177', '14'].includes(String(s.sr_no))).map(s => s.user_symbol);

                setIndices(newIndices);
                setStocks(newStocks);
                setTopTradedEquity(newTopTradedEquity);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching symbols in StocksPage:", error);
                setLoading(false);
            }
        };

        fetchSymbols();
    }, []);

    const handleOpenOptionChain = (symbolName) => {
        navigate(`/option-chain/${symbolName}`);
    };

    const handleOpenChart = async (symbol) => {
        try {
            await api.updateChartSettings({ chartSymbol: symbol });
            navigate('/trade');
        } catch (error) {
            console.error("Error updating chart settings:", error);
        }
    };

    const handleOpenSymbolInfo = (symbol) => {
        navigate(`/symbol/${symbol}`);
    };

    if (loading) {
        return <div className={styles['homepage-container']}>Loading...</div>;
    }

    return (
        <div className={`${styles['homepage-container']} ${theme}`}>
            <div className={styles['section-container']}>
                <div className={styles['header-with-link']}>
                    <h2>Indices</h2>
                </div>
                <div className={styles['scrolling-wrapper']}>
                    {indices.map(symbol => (
                        <StockCard 
                            key={symbol} 
                            symbol={symbol} 
                            onCardClick={() => handleOpenOptionChain(symbol)} 
                        />
                    ))}
                </div>
            </div>

            <div className={styles['section-container']}>
                <div className={styles['header-with-link']}>
                    <h2>Stocks</h2>
                    <Link to="/watchlist?tab=stock" className={styles.viewAllLink}>View All</Link>
                </div>
                <div className={styles['scrolling-wrapper']}>
                    {stocks.map(symbol => (
                        <StockCard 
                            key={symbol} 
                            symbol={symbol} 
                            onCardClick={() => handleOpenChart(symbol)} 
                        />
                    ))}
                </div>
            </div>

            <div className={styles['section-container']} ref={topTradedRef}>
                <div className={styles['header-with-link']}>
                    <h2>Top Traded</h2>
                </div>
                <div className={styles['top-traded-list']}>
                    {topTradedEquity.map(symbol => (
                        <StockRow 
                            key={symbol} 
                            symbol={symbol} 
                            onRowClick={() => handleOpenSymbolInfo(symbol)} 
                            onIconClick={() => handleOpenChart(symbol)} 
                            disableLogo={true}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default StocksPage;
